import React, { useState } from "react";
import { useLang } from "../context/LangContext";
import { useCart } from "../context/CartContext";
import { pickLang, t } from "../lib/i18n";
import { api } from "../lib/api";
import { Trash, Minus, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Quote() {
  const { lang } = useLang();
  const { items, remove, updateQty, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", event_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error(lang === "gr" ? "Άδειο καλάθι" : "Cart is empty"); return; }
    try {
      setBusy(true);
      await api.post("/quote", {
        ...form,
        items: items.map((i) => ({ product_id: i.id, name_en: i.name.en || i.name.gr, name_gr: i.name.gr || i.name.en, quantity: i.qty })),
      });
      toast.success(lang === "gr" ? "Η αίτηση στάλθηκε!" : "Quote request sent!");
      clear();
      setForm({ name: "", email: "", phone: "", company: "", event_date: "", notes: "" });
    } catch {
      toast.error(lang === "gr" ? "Αποτυχία" : "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20" data-testid="quote-page">
      <div className="label-tag mb-4 slash">{t("cart.title", lang)}</div>
      <h1 className="display-font text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tighter mb-12">
        {lang === "gr" ? <>Καλάθι <span className="neon-text">προσφοράς.</span></> : <>Quote <span className="neon-text">basket.</span></>}
      </h1>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {items.length === 0 ? (
            <div className="card-dark p-10 text-center" data-testid="quote-empty">
              <p className="text-gray-400">{t("cart.empty", lang)}</p>
              <Link to="/products" className="btn-primary mt-6 inline-flex">{t("cta.explore", lang)}</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((i, idx) => (
                <div key={i.id} className="card-dark p-4 flex items-center gap-4" data-testid={`quote-item-${idx}`}>
                  <div className="w-20 h-20 bg-[#0a0a0c] shrink-0 overflow-hidden">
                    {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <div className="display-font font-bold">{pickLang(i.name, lang)}</div>
                    <div className="mono-font text-xs text-gray-500 mt-1">{t("cart.qty", lang)}: {i.qty}</div>
                  </div>
                  <div className="flex items-center gap-2 border border-[#2a2a35]">
                    <button onClick={() => updateQty(i.id, i.qty - 1)} className="p-2 hover:text-[#ccff00]" data-testid={`qty-down-${idx}`}><Minus size={14} /></button>
                    <span className="px-3 mono-font">{i.qty}</span>
                    <button onClick={() => updateQty(i.id, i.qty + 1)} className="p-2 hover:text-[#ccff00]" data-testid={`qty-up-${idx}`}><Plus size={14} /></button>
                  </div>
                  <button onClick={() => remove(i.id)} className="p-2 text-gray-500 hover:text-[#ff003c]" data-testid={`remove-${idx}`}><Trash size={18} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="card-dark p-6 h-fit" data-testid="quote-form">
          <h3 className="display-font font-bold text-lg mb-5">{lang === "gr" ? "Στοιχεία επικοινωνίας" : "Your details"}</h3>
          <div className="space-y-3">
            <input required placeholder={t("forms.name", lang)} className="input-dark" value={form.name} onChange={change("name")} data-testid="quote-name" />
            <input required type="email" placeholder={t("forms.email", lang)} className="input-dark" value={form.email} onChange={change("email")} data-testid="quote-email" />
            <input placeholder={t("forms.phone", lang)} className="input-dark" value={form.phone} onChange={change("phone")} data-testid="quote-phone" />
            <input placeholder={t("forms.company", lang)} className="input-dark" value={form.company} onChange={change("company")} data-testid="quote-company" />
            <input type="date" placeholder={t("forms.date", lang)} className="input-dark" value={form.event_date} onChange={change("event_date")} data-testid="quote-date" />
            <textarea placeholder={t("forms.notes", lang)} className="input-dark min-h-[100px]" value={form.notes} onChange={change("notes")} data-testid="quote-notes" />
          </div>
          <button type="submit" disabled={busy || items.length === 0} className="btn-primary w-full mt-5 justify-center" data-testid="quote-submit">
            {busy ? "..." : t("cta.submit_quote", lang)}
          </button>
        </form>
      </div>
    </div>
  );
}
